import { TASK_STATUSES } from './data.js';

/**
 * Definitions PARTAGEES entre l'ecran Cat Houses et son ecran de reglages
 * (settings.tsx, monte par le hub Parametres du shell) : catalogue des
 * pastilles de filtre, catalogues de stats par onglet (Maisons, Tickets),
 * cles de prefs et valeurs par defaut. Un seul endroit, zero duplication.
 *
 * Les prefs sont locales au poste (localStorage via usePref de @taki/ui) :
 *  - "app.cat-houses.filters" : Record<filterId, boolean>, visibilite des
 *    pastilles de statut de l'onglet Tickets (id = la valeur du statut ; cle
 *    absente = visible, seul un `false` explicite masque) ;
 *  - stats : une cle string[] (ids ordonnes, max 3) PAR onglet,
 *    "app.cat-houses.stats.houses" et "app.cat-houses.stats.tasks" : chaque
 *    onglet affiche ses propres stats, un plafond de 3 partage entre les deux
 *    n'aurait pas de sens (meme decoupage que Cat Manager Six).
 */

/* ---- Filtres (zone 4, onglet Tickets seulement) --------------------------- */

/** Cle de pref : visibilite des pastilles de statut de l'onglet Tickets. */
export const FILTERS_PREF_KEY = 'app.cat-houses.filters';

/**
 * Defaut filtres : toutes les pastilles visibles. La pastille 'Tous' n'est pas
 * dans le catalogue : c'est le repli, elle reste toujours affichee.
 */
export const DEFAULT_FILTERS: Record<string, boolean> = Object.fromEntries(
  TASK_STATUSES.map((option) => [option.value, true]),
);

/* ---- Stats d'en-tete (zone 3), une cle par onglet -------------------------- */

/** Nombre maximal de stats affichees par onglet (contrat des cles de stats). */
export const STATS_MAX = 3;

/** Ids des statistiques disponibles sur l'onglet Maisons. */
export type HouseStatId = 'houses' | 'housed' | 'housed-outside';

/**
 * Catalogue des stats de l'onglet Maisons. La SELECTION (max 3) est editable
 * dans settings.tsx ; l'ordre d'affichage suit ce catalogue et les valeurs
 * restent calculees des donnees reelles par l'ecran.
 */
export const HOUSE_STAT_DEFS: ReadonlyArray<{ id: HouseStatId; label: string }> = [
  { id: 'houses', label: 'maisons' },
  { id: 'housed', label: 'chats loges' },
  { id: 'housed-outside', label: 'chats en sortie' },
];

/** Cle de pref : ids ordonnes des stats affichees sur l'onglet Maisons. */
export const HOUSES_STATS_PREF_KEY = 'app.cat-houses.stats.houses';

/** Defaut stats maisons : les 3 premieres du catalogue. */
export const DEFAULT_HOUSE_STATS: string[] = HOUSE_STAT_DEFS.slice(0, STATS_MAX).map(
  (def) => def.id,
);

/**
 * Ids des statistiques disponibles sur l'onglet Tickets : un compteur par
 * statut, memes valeurs que `TaskStatus` (l'ecran indexe ses comptes avec).
 */
export type TaskStatId = 'ouvert' | 'en_cours' | 'clos';

/** Catalogue des stats de l'onglet Tickets. Memes regles que celui des maisons. */
export const TASK_STAT_DEFS: ReadonlyArray<{ id: TaskStatId; label: string }> = [
  { id: 'ouvert', label: 'ouverts' },
  { id: 'en_cours', label: 'en cours' },
  { id: 'clos', label: 'fermes' },
];

/** Cle de pref : ids ordonnes des stats affichees sur l'onglet Tickets. */
export const TASKS_STATS_PREF_KEY = 'app.cat-houses.stats.tasks';

/** Defaut stats tickets : les 3 premieres du catalogue. */
export const DEFAULT_TASK_STATS: string[] = TASK_STAT_DEFS.slice(0, STATS_MAX).map(
  (def) => def.id,
);
